import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Pagination from '@/components/ui/Pagination'
import SearchInput from '@/components/ui/SearchInput'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import {
  MOST_TRADED_MULTI_SECTOR_AVAILABLE,
  MOST_TRADED_SEARCH_AVAILABLE,
  MOST_TRADED_SORT_AVAILABLE,
} from '@/features/brokers/api/brokerGaps'
import { useBusyNames, useSectorFlow } from '@/features/brokers/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { resolveViewState } from '@/lib/view-state'
import type { BrokerPeriod } from '@/schemas/broker'
import { pageSizeSchema } from '@/schemas/table'
import { mostTradedSearchSchema, toBusyNamesQuery } from '@/screens/brokers/search'
import BusyNamesTable from '@/screens/brokers/components/most-traded/BusyNamesTable'
import MostTradedSectorMenu from '@/screens/brokers/components/most-traded/MostTradedSectorMenu'
import { asLoadState } from '@/screens/brokers/components/broker/broker-format'
import BrokerPeriodPicker from '@/screens/brokers/components/broker/BrokerPeriodPicker'
import WindowNote from '@/screens/brokers/components/broker/WindowNote'
import { loadMostTraded } from '@/screens/brokers/MostTradedScreen.loader'

const HEADER = <TitleRow title="Most Traded" />

/** The table's ten rows, two lines each (`SymbolCell` is a symbol over its company's name). */
const SKELETON_ROWS = 10

function MostTradedPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading most traded…"
      header={HEADER}
      pageTitle="Most Traded"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-11 w-60 rounded-md lg:h-9" />
          <Skeleton className="ml-auto h-11 w-72 rounded-md lg:h-9" />
        </div>
        {/* The "As of …" line, one body-xs row. */}
        <Skeleton className="h-4 w-32 rounded-xs" />
        <div>
          <DataTableSkeleton rows={SKELETON_ROWS} columns={6} rowHeight="lg" />
          {/* `Pagination`'s own row, 24 under the table: rows-per-page on the left, pages right. */}
          <div className="mt-6 flex h-8 items-center justify-between gap-3">
            <Skeleton className="h-4 w-32 rounded-xs" />
            <Skeleton className="h-8 w-56 rounded-sm" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

function MostTradedError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Busy names did not load" />
}

export const Route = createFileRoute('/_app/brokers/most-traded')({
  validateSearch: mostTradedSearchSchema,
  loader: loadMostTraded,
  pendingComponent: MostTradedPending,
  errorComponent: MostTradedError,
  component: MostTradedScreen,
})

/**
 * Brokers · Most traded — "Busy names" in the API, drawn as the revamp mock does: a search field
 * on the left, the sector menu (several at once, with Apply), the period row and its calendar on
 * the right, then one table of Symbol, Trades, Turnover, % change and Price, with sortable heads.
 *
 * What mm-be serves is `turnover ÷ market cap`, ranked descending, so "% of its size" stays as
 * the ranking column. Trades and Turnover have no field yet, a typed search and a sort have no
 * param, and several sectors at once has no list param: each is present and, when asked for,
 * answers with the generic "Not available yet" (`brokerGaps.ts`, `asks/most-traded.md`). The
 * default view, one sector and the period row are served.
 */
function MostTradedScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()

  // Goes through `toBusyNamesQuery`, not `search` itself — see the picker's
  // own comment in search.ts for why the two are not the same object.
  const data = useBusyNames(toBusyNamesQuery(search))
  // A failed background refetch keeps whatever page of names is already on
  // screen; only a data-less query reads as failed/offline (CRS-ST-2).
  const view = asLoadState(resolveViewState(data, { online }))

  /**
   * The sector list has no dedicated source of its own — `BusyNameSerializer`
   * carries no sector field per row, and there is no `/brokers/sectors/`
   * endpoint. Sector flow's own overview already names every sector the
   * broker slice knows about (`SectorFlowRow.sector`, matched server-side by
   * `company.sector.name`, exactly what `busy-names?sector=` filters against),
   * so it stands in here rather than inventing a second list. Fixed at
   * `'today'` because this call exists only to populate a menu, not to show a
   * number — if the day's precompute is empty the menu is briefly short
   * rather than the page failing.
   */
  const sectorSource = useSectorFlow('today')
  const sectorNames = (sectorSource.data?.sectors ?? []).map((row) => row.sector)

  const onPeriodChange = (next: BrokerPeriod) => {
    void navigate({ search: (prev) => ({ ...prev, period: next, page: 1 }) })
  }

  // The ticked sectors. One sector (or all) is a read mm-be has, so it lives in the URL; two or
  // more is a pick it cannot answer, so it is held here and the body says so.
  const [multi, setMulti] = useState<string[] | null>(null)
  const chosen = multi ?? (search.sector === undefined ? null : [search.sector])

  const onSectorApply = (next: string[] | null) => {
    const several = next !== null && next.length > 1
    setMulti(several ? next : null)
    const sector = next !== null && next.length === 1 ? next[0] : undefined
    void navigate({ search: (prev) => ({ ...prev, sector, page: 1 }) })
  }

  const [query, setQuery] = useState('')
  const [sortBy, setSortBy] = useState<string | undefined>(undefined)
  const [sortDesc, setSortDesc] = useState(false)

  const unavailable =
    multi !== null && !MOST_TRADED_MULTI_SECTOR_AVAILABLE
      ? 'Several sectors at once'
      : query.trim() !== '' && !MOST_TRADED_SEARCH_AVAILABLE
        ? 'Search by stock'
        : sortBy !== undefined && !MOST_TRADED_SORT_AVAILABLE
          ? 'This sort'
          : null

  const goToPage = (page: number) => {
    void navigate({ search: (prev) => ({ ...prev, page }) })
  }

  const goToPageSize = (pageSize: number) => {
    void navigate({
      search: (prev) => ({ ...prev, pageSize: pageSizeSchema.parse(pageSize), page: 1 }),
    })
  }

  return (
    <ScreenLayout header={HEADER} pageTitle="Most Traded">
      {/* Toolbar and table sit 16 apart (the pages revamp's one rhythm); the pager keeps its own
          24 under the table. */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            aria-label="Search a stock"
            placeholder="Search a stock"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
            }}
            className="w-full sm:w-60"
          />
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <MostTradedSectorMenu sectors={sectorNames} chosen={chosen} onApply={onSectorApply} />
            <BrokerPeriodPicker value={search.period} onChange={onPeriodChange} />
          </div>
        </div>

        {/* The session the page covers: the date only, since the clock half is not on the wire. */}
        <WindowNote window={data.data?.window} />

        {/* A <div>, not a fragment: a fragment's children are children of the flex stack, so its
            `gap-4` fell between the table and its pager and would add to `Pagination`'s own 24px. */}
        <div>
          <BusyNamesTable
            rows={data.data?.rows ?? []}
            view={view}
            onRetry={() => void data.refetch()}
            retrying={data.isFetching}
            savedAt={data.dataUpdatedAt}
            sortBy={sortBy}
            sortDesc={sortDesc}
            onSortChange={(next, desc) => {
              setSortBy(next)
              setSortDesc(desc)
            }}
            unavailable={unavailable}
          />

          {data.data === undefined || unavailable !== null ? null : (
            <Pagination
              page={data.data.page}
              pageSize={data.data.pageSize}
              total={data.data.total}
              onPageChange={goToPage}
              onPageSizeChange={goToPageSize}
              unit="stocks"
            />
          )}
        </div>
      </div>
    </ScreenLayout>
  )
}
